// Helper function to get relevant learning objective examples based on subject and grade level
export function getLearningObjectiveExamples(subject, gradeLevel) {
  // Early years examples (nursery through Year 2)
  if (['nursery', 'preK', 'kindergarten', 'grade1', 'grade2'].includes(gradeLevel)) {
    switch (subject) {
      case 'Mathematics':
        return [
          "To recognise and name common 2D shapes (circle, square, triangle)",
          "To count reliably up to 20 objects",
          "To understand the concept of one more and one less"
        ];
      case 'Science':
        return [
          "To identify and name a variety of common animals",
          "To observe changes across the four seasons",
          "To distinguish between an object and the material it is made from"
        ];
      case 'English':
        return [
          "To blend sounds in unfamiliar words using phonics",
          "To form lower-case and capital letters correctly",
          "To sequence sentences to form short narratives"
        ];
      default:
        return [
          "To identify and name...",
          "To describe and compare...",
          "To use simple tools and techniques..."
        ];
    }
  }
  // Lower KS2 (Years 3-4)
  else if (['grade3', 'grade4'].includes(gradeLevel)) {
    switch (subject) {
      case 'Mathematics':
        return [
          "To identify and name unit fractions (1/2, 1/4, 1/3) as parts of a whole",
          "To add and subtract fractions with the same denominator",
          "To recognise equivalent fractions (e.g., 1/2 = 2/4)"
        ];
      case 'Science':
        return [
          "To identify that animals need the right types of nutrition",
          "To investigate how sounds are made and how they travel",
          "To compare and group materials according to whether they are solids, liquids or gases"
        ];
      case 'English':
        return [
          "To use conjunctions to express time, place and cause",
          "To use paragraphs to organise ideas around a theme",
          "To evaluate and edit writing by proposing changes to grammar and vocabulary"
        ];
      case 'History':
        return [
          "To understand chronology by placing events on a timeline",
          "To use historical sources to answer questions about the past",
          "To identify similarities and differences between ways of life in different periods"
        ];
      default:
        return [
          "To investigate and explain...",
          "To compare and classify...",
          "To demonstrate understanding of..."
        ];
    }
  }
  // Upper KS2 (Years 5-6)
  else if (['grade5', 'grade6'].includes(gradeLevel)) {
    switch (subject) {
      case 'Mathematics':
        return [
          "To multiply proper fractions and mixed numbers by whole numbers",
          "To solve problems involving percentages and decimal equivalents",
          "To calculate the area of rectangles and estimate the area of irregular shapes"
        ];
      case 'Science':
        return [
          "To describe the life process of reproduction in some plants and animals",
          "To explain that unsupported objects fall because of the force of gravity",
          "To recognise that light appears to travel in straight lines"
        ];
      case 'English':
        return [
          "To use relative clauses beginning with who, which, where, when, whose, that",
          "To use modal verbs to indicate degrees of possibility",
          "To identify audience and purpose for writing, selecting appropriate forms"
        ];
      default:
        return [
          "To explain and analyse...",
          "To develop techniques and skills...",
          "To evaluate and improve..."
        ];
    }
  }
  // KS3 (Years 7-9)
  else if (['grade7', 'grade8', 'grade9'].includes(gradeLevel)) {
    switch (subject) {
      case 'Mathematics':
        return [
          "To use algebraic methods to solve linear equations in one variable",
          "To work with coordinates in all four quadrants",
          "To calculate and interpret the mean as an average"
        ];
      case 'Science':
        return [
          "To explain the differences between atoms, elements and compounds",
          "To relate the structure of cells to their functions",
          "To describe forces as pushes or pulls arising from the interaction between two objects"
        ];
      case 'English':
        return [
          "To analyse how writers use language and structure to achieve effects",
          "To write for a wide range of purposes and audiences",
          "To use Standard English confidently in formal speaking and writing"
        ];
      default:
        return [
          "To analyse and evaluate...",
          "To develop and apply...",
          "To investigate and explore..."
        ];
    }
  }
  // KS4 (Years 10-11)
  else if (['grade10', 'grade11'].includes(gradeLevel)) {
    switch (subject) {
      case 'Mathematics':
        return [
          "To solve quadratic equations algebraically by factorising",
          "To calculate and interpret conditional probabilities",
          "To interpret and construct tables, charts and diagrams for grouped data"
        ];
      case 'Biology':
        return [
          "To explain how the structure of DNA allows for self-replication",
          "To relate photosynthesis and respiration to the biochemical processes in cells",
          "To evaluate the impact of environmental changes on a given ecosystem"
        ];
      case 'English':
        return [
          "To critically compare and contrast texts, exploring deeper meanings",
          "To craft sophisticated narratives using a range of literary techniques",
          "To analyse how context influences the meaning and interpretation of texts"
        ];
      default:
        return [
          "To critically evaluate...",
          "To synthesise information from...",
          "To demonstrate comprehensive understanding of..."
        ];
    }
  }
  // KS5 (Years 12-13)
  else if (['grade12'].includes(gradeLevel)) {
    switch (subject) {
      case 'Mathematics':
        return [
          "To apply differentiation techniques to solve optimisation problems",
          "To construct mathematical proofs using appropriate notation",
          "To model real-world situations using differential equations"
        ];
      case 'Biology':
        return [
          "To evaluate the role of DNA technology in modern medicine",
          "To analyse data from complex experimental procedures",
          "To critically discuss the ethical implications of genetic engineering"
        ];
      case 'Chemistry':
        return [
          "To predict reaction mechanisms based on molecular structure",
          "To analyse spectroscopic data to determine molecular structures",
          "To evaluate the environmental impact of industrial chemical processes"
        ];
      default:
        return [
          "To critically analyse and evaluate...",
          "To synthesise complex information to...",
          "To demonstrate mastery of..."
        ];
    }
  }
  // Higher education
  else if (['foundation', 'undergraduate', 'postgraduate'].includes(gradeLevel)) {
    return [
      "To critically evaluate theoretical frameworks in relation to...",
      "To synthesise research findings to develop new approaches to...",
      "To demonstrate comprehensive understanding of methodological approaches in..."
    ];
  }
  // Default for other grade levels
  else {
    return [
      "To understand and explain key concepts related to...",
      "To develop and apply skills in...",
      "To analyse and evaluate information about..."
    ];
  }
}
// Helper function to get learning objective guidance text
export function getLearningObjectiveGuidance(gradeLevel) {
  // Early years (EYFS)
  if (['nursery', 'preK', 'kindergarten'].includes(gradeLevel)) {
    return "For early years (2-5), focus on exploratory, play-based learning objectives that develop foundational skills. Use simple, observable actions (e.g., 'To recognise', 'To name', 'To sort').";
  }
  // KS1 (Years 1-2)
  else if (['grade1', 'grade2'].includes(gradeLevel)) {
    return "For KS1 (5-7 years), create specific, achievable objectives using action verbs like 'identify', 'describe', 'sort', 'count', or 'name'. Focus on concrete understanding.";
  }
  // Lower KS2 (Years 3-4)
  else if (['grade3', 'grade4'].includes(gradeLevel)) {
    return "For lower KS2 (7-9 years), objectives should build on concrete understanding while introducing more abstract concepts. Use verbs like 'explain', 'compare', 'classify', or 'apply'.";
  }
  // Upper KS2 (Years 5-6)
  else if (['grade5', 'grade6'].includes(gradeLevel)) {
    return "For upper KS2 (9-11 years), objectives should encourage deeper thinking and application. Use verbs like 'analyse', 'investigate', 'evaluate', 'create', or 'justify'.";
  }
  // KS3 (Years 7-9)
  else if (['grade7', 'grade8', 'grade9'].includes(gradeLevel)) {
    return "For KS3 (11-14 years), focus on developing subject-specific skills and conceptual understanding. Use verbs like 'analyse', 'evaluate', 'interpret', 'synthesise', or 'critique'.";
  }
  // KS4 (Years 10-11)
  else if (['grade10', 'grade11'].includes(gradeLevel)) {
    return "For KS4 (14-16 years), align objectives with GCSE assessment criteria. Focus on application, analysis, and evaluation skills using precise subject terminology.";
  }
  // KS5 and higher education
  else {
    return "For post-16 education, create objectives that develop higher-order thinking, independent research, and critical analysis. Align with relevant qualification frameworks.";
  }
}
// Helper function to get pedagogical approach guidance
export function getPedagogicalApproach(gradeLevel) {
  // Early years and KS1
  if (['nursery', 'preK', 'kindergarten', 'grade1', 'grade2'].includes(gradeLevel)) {
    return "Pedagogical approach: Use concrete-pictorial-abstract progression. Begin with hands-on experiences and manipulatives, move to visual representations, then introduce abstract concepts. Assessment should include observation, questioning, and simple self-assessment.";
  }
  // KS2
  else if (['grade3', 'grade4', 'grade5', 'grade6'].includes(gradeLevel)) {
    return "Pedagogical approach: Follow concrete-pictorial-abstract progression. Include varied assessment methods such as mini-whiteboards, hinge questions, peer assessment, and exit tickets. Consider differentiation through scaffolding and extension tasks.";
  }
  // KS3 and KS4
  else if (['grade7', 'grade8', 'grade9', 'grade10', 'grade11'].includes(gradeLevel)) {
    return "Pedagogical approach: Balance direct instruction with inquiry-based learning. Use formative assessment strategies including self-assessment against success criteria, peer review, and targeted questioning. Differentiate through task design, questioning, and outcome.";
  }
  // KS5 and higher education
  else {
    return "Pedagogical approach: Emphasise independent learning and critical thinking. Assessment should include opportunities for metacognition, self-reflection, and application to novel contexts. Differentiate through resource complexity, outcome expectations, and support level.";
  }
}